import { type ReactNode, useLayoutEffect, useState } from 'react'

import { Box, Text } from '@enechain/ecloud-designsystem'

import { pinAreaAndPeriodFirst } from '~/components/common/filters/filterItemOrder'

/** 条件 1 つ = ラベル + 部品。ラベルの無いもの (状態のピル等) は部品だけでマスを使う。 */
export type FilterGridItem = {
  /** 並べ替え (`pinAreaAndPeriodFirst`) の判定にも使う。エリアは `area`。 */
  key: string
  label?: ReactNode
  control: ReactNode
}

/** ラベル → 部品の間。 */
const LABEL_GAP_PX = 8
/** 列の間 (縦線は置かない)。 */
const COLUMN_GAP_PX = 20
/** 行の間 (UI 辞書 toolbar-collapsible.md「開いた条件のカード」12px)。 */
const ROW_GAP_PX = 12
/** 1 マスの部品の最小幅。これより狭くなる列数は選ばない。 */
const MIN_CONTROL_WIDTH_PX = 200
/** これより低い要素 (隠した input・読み上げ用の 1px のラベルなど) は部品の 1 行目に数えない。 */
const MIN_FIRST_LINE_PX = 12
/** これより高い要素は「1 行目」ではなく折り返した部品全体として扱う。 */
const MAX_FIRST_LINE_PX = 48

/**
 * 部品の 1 行目の高さ = 部品の上端にそろっている最初の要素の高さ (40px の select・日付の送り、
 * 24px のチェックボックス・ラジオ)。折り返した部品 (日付のまとまりの下の当日 / 翌日、発注主体の
 * 下の発注できるエリアの文) は 1 行目だけを見る。React mock の FilterGrid と同じ求め方。
 */
const firstLineHeight = (control: HTMLElement): number => {
  const top = control.getBoundingClientRect().top
  for (const element of control.querySelectorAll<HTMLElement>('*')) {
    const rect = element.getBoundingClientRect()
    if (rect.height < MIN_FIRST_LINE_PX || rect.height > MAX_FIRST_LINE_PX)
      continue
    if (Math.abs(rect.top - top) <= 1) return rect.height
  }
  return 0
}

/** 部品の最小幅 (min-content。折り返さない日付のまとまり・幅を決めた select)。 */
const minContentWidth = (control: HTMLElement): number => {
  const width = control.style.width
  control.style.width = 'min-content'
  const minWidth = control.offsetWidth
  control.style.width = width
  return minWidth
}

type Props = {
  items: readonly FilterGridItem[]
  /** 渡した順のまま並べる (既定は `pinAreaAndPeriodFirst` でエリア → 期間関連 → 残り)。 */
  keepOrder?: boolean
}

/**
 * 開いた条件のカードの中身。「ラベル | 部品」のマスを左 → 右、入らなければ次の行へ流す
 * (UI 辞書 toolbar-collapsible.md「開いた条件のカード」)。
 *
 * - 列の幅は等分 (`repeat(N, 1fr)`)。ラベルの列の幅はカードで 1 つ (いちばん長いラベル)
 * - 列の数 = 幅に入る数 (1 マス = ラベルの列 + 8px + 部品の最小幅) と項目数の小さい方。
 *   部品の最小幅は、折り返さない部品 (「<」日付「>」のまとまり等) の min-content と 200px の大きい方
 * - ラベルは部品の 1 行目の縦の真ん中にそろえる (マスは上端そろえ、ラベルに上の余白)。
 *   2026-10-26 デザイナー「各項目のラベルは、右にくる部品と縦位置センターアラインしたい」
 * - 列の間 20px・行の間 12px。部品は 40px (md) のまま使う (32px にしない)
 * - エリアと期間関連は先頭に寄せる (`pinAreaAndPeriodFirst`)
 *
 * [DS差異] React mock の FilterGrid にある「列数ごとに実際に並べて高さを比べる」測定・
 * 幅の広い部品 (`isWide`) の 2 列化・従属項目は持ち込んでいない (必要になった画面で足す)。
 * 1 行目にそろえるラベルと、部品の min-content を列の数に入れる所だけ React mock と同じ。
 */
export const FilterGrid = ({
  items,
  keepOrder = false,
}: Props): React.ReactElement => {
  const ordered = keepOrder ? [...items] : pinAreaAndPeriodFirst(items)
  const [grid, setGrid] = useState<HTMLElement | null>(null)
  const [labelWidth, setLabelWidth] = useState(0)
  const [columns, setColumns] = useState(1)
  const itemCount = ordered.length

  useLayoutEffect(() => {
    if (grid === null) return undefined
    const sizer = grid.querySelector<HTMLElement>('[data-grid-sizer]')
    const measure = (): void => {
      // 閉じている間 (hidden で幅 0) は測らない
      if (grid.clientWidth === 0) return
      const nextLabelWidth = sizer === null ? 0 : Math.ceil(sizer.offsetWidth)
      const controls = [
        ...grid.querySelectorAll<HTMLElement>('[data-grid-control]'),
      ]
      const minControlWidth = Math.max(
        MIN_CONTROL_WIDTH_PX,
        ...controls.map(minContentWidth),
      )
      const cellWidth = nextLabelWidth + LABEL_GAP_PX + minControlWidth
      const byWidth = Math.max(
        1,
        Math.floor(
          (grid.clientWidth + COLUMN_GAP_PX) / (cellWidth + COLUMN_GAP_PX),
        ),
      )
      const nextColumns = Math.max(1, Math.min(byWidth, itemCount))
      setLabelWidth((current) =>
        current === nextLabelWidth ? current : nextLabelWidth,
      )
      setColumns((current) => (current === nextColumns ? current : nextColumns))
      // ラベルを部品の 1 行目の真ん中へ (上の余白 = (1 行目の高さ - ラベルの行の高さ) / 2)
      for (const cell of grid.querySelectorAll<HTMLElement>(
        '[data-grid-cell]',
      )) {
        const label = cell.querySelector<HTMLElement>('[data-grid-label]')
        const control = cell.querySelector<HTMLElement>('[data-grid-control]')
        if (label === null || control === null) continue
        const lineHeight = parseFloat(getComputedStyle(label).lineHeight)
        const offset = Number.isFinite(lineHeight)
          ? Math.max(0, (firstLineHeight(control) - lineHeight) / 2)
          : 0
        label.style.paddingTop = `${offset}px`
      }
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(grid)
    if (sizer !== null) observer.observe(sizer)
    return () => observer.disconnect()
    // 列の数・ラベルの幅が変わった後 (部品の折り返しが変わる) にも測り直す
  }, [grid, itemCount, columns, labelWidth])

  return (
    <Box
      ref={setGrid}
      position="relative"
      alignItems="start"
      rowGap={`${ROW_GAP_PX}px`}
      columnGap={`${COLUMN_GAP_PX}px`}
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      display="grid"
      width="100%"
      minWidth={0}
      padding={3}
      data-columns={columns}
    >
      {/* ラベルの列の幅を測る見えない箱 (全ラベルを縦に積む。幅 = いちばん長いラベル) */}
      <Box
        position="absolute"
        top={0}
        left={0}
        overflow="hidden"
        width="max-content"
        height={0}
        visibility="hidden"
        pointerEvents="none"
        aria-hidden
        data-grid-sizer=""
      >
        {ordered.map(
          (item) =>
            item.label !== undefined && (
              <Text key={item.key} textStyle="filterLabel" whiteSpace="nowrap">
                {item.label}
              </Text>
            ),
        )}
      </Box>
      {ordered.map((item) => (
        <Box
          key={item.key}
          // 横に並ぶマスは上端にそろえ、ラベルは部品の 1 行目の真ん中 (上の測定で付ける上の余白)
          alignItems="start"
          columnGap={`${LABEL_GAP_PX}px`}
          gridTemplateColumns={
            item.label === undefined
              ? 'minmax(0, 1fr)'
              : `${labelWidth}px minmax(0, 1fr)`
          }
          display="grid"
          minWidth={0}
          data-grid-cell={item.key}
        >
          {item.label !== undefined && (
            <Text
              textStyle="filterLabel"
              color="Text.highEmphasis"
              whiteSpace="nowrap"
              data-grid-label=""
            >
              {item.label}
            </Text>
          )}
          <Box
            justifySelf="start"
            minWidth={0}
            maxWidth="100%"
            {...(item.label !== undefined && { 'data-grid-control': '' })}
          >
            {item.control}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
